import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { type ApiResponse, ApiError, ErrorCodes } from '@/types/api-response.types';
import { i18n } from '@/lib/i18n';
import { CAREERTREE_CHECKOUT_RETURN_STORAGE_KEY } from '@/lib/careertree-onboarding-intent';

/**
 * Unified HTTP Client
 * - Handles authentication automatically
 * - Supports token refresh on 401
 * - Single source of truth for all API calls
 */
class HttpClient {
  private static instance: HttpClient;
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: '', // Use Vite proxy
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  static getInstance(): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient();
    }
    return HttpClient.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor - add auth token and language header
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Accept-Language'] = i18n.language || 'en';
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh and API errors
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Check if response is in our standard format
        if (response.data && 'status' in response.data && 'timestamp' in response.data) {
          const apiResponse = response.data as ApiResponse;
          if (apiResponse.status === 'error') {
            // Transform API error to rejection
            const apiError = new ApiError(
              apiResponse.error?.code || ErrorCodes.SERVER_INTERNAL_ERROR,
              apiResponse.error?.message || 'An error occurred',
              apiResponse.error?.details,
              apiResponse.error?.field
            );
            return Promise.reject(apiError);
          }
          // Extract data from successful response
          response.data = apiResponse.data;
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Check if error response is in our standard format
        if (error.response?.data && 'status' in error.response.data) {
          const apiResponse = error.response.data as ApiResponse;
          if (apiResponse.error) {
            const apiError = new ApiError(
              apiResponse.error.code,
              apiResponse.error.message,
              apiResponse.error.details,
              apiResponse.error.field
            );

            // Handle auth errors specifically
            if (apiResponse.error.code === ErrorCodes.AUTH_TOKEN_EXPIRED ||
                apiResponse.error.code === ErrorCodes.AUTH_UNAUTHORIZED) {
              // Try token refresh
              if (!originalRequest._retry) {
                if (this.isRefreshing) {
                  // If already refreshing, queue this request
                  return new Promise((resolve) => {
                    this.refreshSubscribers.push((token: string) => {
                      originalRequest.headers!.Authorization = `Bearer ${token}`;
                      resolve(this.axiosInstance(originalRequest));
                    });
                  });
                }

                originalRequest._retry = true;
                this.isRefreshing = true;

                try {
                  const refreshToken = useAuthStore.getState().refreshToken;
                  if (!refreshToken) {
                    throw new Error('No refresh token available');
                  }

                  const response = await this.axiosInstance.post('/api/v1/auth/refresh', {
                    refreshToken
                  });

                  const { accessToken, refreshToken: newRefreshToken } = this.getRefreshTokens(response.data);
                  useAuthStore.getState().updateTokens(accessToken, newRefreshToken);

                  // Retry all queued requests with new token
                  this.refreshSubscribers.forEach(callback => callback(accessToken));
                  this.refreshSubscribers = [];

                  originalRequest.headers!.Authorization = `Bearer ${accessToken}`;
                  return this.axiosInstance(originalRequest);
                } catch (refreshError) {
                  // Refresh failed - logout and redirect
                  useAuthStore.getState().logout();
                  sessionStorage.removeItem(CAREERTREE_CHECKOUT_RETURN_STORAGE_KEY);
                  window.location.replace('/login');
                  return Promise.reject(refreshError);
                } finally {
                  this.isRefreshing = false;
                }
              }
            }

            return Promise.reject(apiError);
          }
        }

        // Legacy 401 handling for backward compatibility
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                originalRequest.headers!.Authorization = `Bearer ${token}`;
                resolve(this.axiosInstance(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = useAuthStore.getState().refreshToken;
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const response = await this.axiosInstance.post('/api/v1/auth/refresh', {
              refreshToken
            });

            const { accessToken, refreshToken: newRefreshToken } = this.getRefreshTokens(response.data);
            useAuthStore.getState().updateTokens(accessToken, newRefreshToken);

            // Retry all queued requests with new token
            this.refreshSubscribers.forEach(callback => callback(accessToken));
            this.refreshSubscribers = [];

            originalRequest.headers!.Authorization = `Bearer ${accessToken}`;
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh failed - logout and redirect
            useAuthStore.getState().logout();
            sessionStorage.removeItem(CAREERTREE_CHECKOUT_RETURN_STORAGE_KEY);
            window.location.replace('/login');
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getRefreshTokens(
    payload: ApiResponse<{ accessToken: string; refreshToken: string }> | { accessToken: string; refreshToken: string }
  ): { accessToken: string; refreshToken: string } {
    if ('accessToken' in payload) {
      return payload;
    }

    if (payload.data) {
      return payload.data;
    }

    throw new Error('Invalid refresh token response');
  }

  // Standard HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  /**
   * Get axios instance for advanced use cases
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Export singleton instance
export const httpClient = HttpClient.getInstance();
